'use client';

import { useEffect, useState } from 'react';
import type { Property, RatePlan } from '@/core/domain/entities';
import AvailabilityCalendar from './AvailabilityCalendar';
import { strings } from '@/content/strings';
import { useRatePlanSelection } from './RatePlanProvider';
import { useAvailabilityContext } from './AvailabilityProvider';

type AvailabilitySectionProps = {
  property: Property;
  ratePlans?: RatePlan[];
  blocked: { start: string; end: string; source: 'booking' | 'ical' | 'manual' }[];
  minNightOverrides: { start: string; end: string; minNights: number }[];
};

export default function AvailabilitySection({
  property,
  ratePlans = [],
  blocked,
  minNightOverrides,
}: AvailabilitySectionProps) {
  const propertyStrings = strings.screens.property;
  const availability = useAvailabilityContext();
  const selection = useRatePlanSelection();
  const selectedRatePlanId = selection?.selectedRatePlanId ?? ratePlans[0]?.id ?? '';
  const activePlan = ratePlans.find((plan) => plan.id === selectedRatePlanId);
  const minLeadDays = activePlan?.minLeadDays ?? 0;
  const [isStacked, setIsStacked] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia('(max-width: 1024px)');
    const update = () => setIsStacked(media.matches);
    update();
    if (media.addEventListener) {
      media.addEventListener('change', update);
      return () => media.removeEventListener('change', update);
    }
    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  return (
    <div id="availability" className="bg-card rounded-2xl shadow-md p-8 space-y-6 border border-border text-card-foreground">
      <h2 className="text-2xl font-semibold text-foreground">{propertyStrings.availability}</h2>
      <AvailabilityCalendar
        blocked={availability?.blocked ?? blocked}
        minNights={property.minNights}
        maxNights={property.maxNights}
        minLeadDays={minLeadDays}
        minNightOverrides={availability?.minNightOverrides ?? minNightOverrides}
        monthsToShow={isStacked ? 1 : 2}
        layout={isStacked ? 'stacked' : 'inline'}
      />
    </div>
  );
}
